//----------------------------------------------------------------------------------------------------------------------
// Mouse Input Resource Access
//----------------------------------------------------------------------------------------------------------------------

// Interfaces
import type { ButtonState, MouseDevice, MouseInputState, Position } from '../../interfaces/input.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Callback type for mouse device connection events
 */
export type MouseDeviceCallback = (device : MouseDevice) => void;

/**
 * Callback type for mouse input events
 */
export type MouseInputCallback = (device : MouseDevice, state : MouseInputState) => void;

/**
 * Responsible for tracking mouse state and notifying subscribers of changes.
 */
export class MouseInputPlugin
{
    private _targetElement : HTMLElement;
    private _mouseDevice : MouseDevice;
    private _buttonState : Record<string, ButtonState> = {};
    private _axesState : Record<string, number> = {};
    private _position : Position = {
        absolute: { x: 0, y: 0 },
        relative: { x: 0, y: 0 },
    };

    private _wheelState = {
        deltaX: 0,
        deltaY: 0,
        deltaZ: 0,
        deltaMode: 0,
    };

    // Callbacks
    private _onDeviceConnected ?: MouseDeviceCallback;
    private _onInputChanged ?: MouseInputCallback;

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Create a new MouseInputPlugin
     *
     * @param targetElement - DOM element to attach mouse listeners to (defaults to document.body)
     */
    constructor(targetElement : HTMLElement = document.body)
    {
        this._targetElement = targetElement;

        // Initialize mouse device
        this._mouseDevice = {
            id: 'mouse-0',
            name: 'Mouse',
            type: 'mouse',
            connected: true,
        };

        // Set up event listeners
        this._setupMouseEvents();

        // Initialize axes to zero
        this._axesState['axis-x'] = 0;
        this._axesState['axis-y'] = 0;

        // Notify that device is connected (on next tick to allow callback registration)
        setTimeout(() => this._notifyDeviceConnected(), 0);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Register a callback for device connected events
     *
     * @param callback
     */
    public onDeviceConnected(callback : MouseDeviceCallback) : void
    {
        this._onDeviceConnected = callback;
    }

    /**
     * Register a callback for input changed events
     *
     * @param callback
     */
    public onInputChanged(callback : MouseInputCallback) : void
    {
        this._onInputChanged = callback;
    }

    /**
     * Get the current mouse state
     */
    public getState() : MouseInputState
    {
        return {
            type: 'mouse',
            buttons: { ...this._buttonState },
            axes: { ...this._axesState },
            position: {
                absolute: { ...this._position.absolute },
                relative: { ...this._position.relative },
            },
            wheel: { ...this._wheelState },
        };
    }

    /**
     * Get the mouse device
     */
    public getDevice() : MouseDevice
    {
        return { ...this._mouseDevice };
    }

    /**
     * Tears down the mouse resource access and cleans up event listeners
     */
    public $teardown() : Promise<void>
    {
        this._targetElement.removeEventListener('pointerdown', this._handlePointerDown);
        this._targetElement.removeEventListener('pointerup', this._handlePointerUp);
        this._targetElement.removeEventListener('pointermove', this._handlePointerMove);
        this._targetElement.removeEventListener('wheel', this._handleMouseWheel);

        return Promise.resolve();
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Set up pointer and wheel event listeners. We use pointer events instead of mouse events
     * because BabylonJS calls preventDefault() on pointerdown, which suppresses the
     * corresponding mousedown/mouseup per the Pointer Events spec.
     */
    private _setupMouseEvents() : void
    {
        // Bind handlers to this instance
        this._handlePointerDown = this._handlePointerDown.bind(this);
        this._handlePointerUp = this._handlePointerUp.bind(this);
        this._handlePointerMove = this._handlePointerMove.bind(this);
        this._handleMouseWheel = this._handleMouseWheel.bind(this);

        this._targetElement.addEventListener('pointerdown', this._handlePointerDown);
        this._targetElement.addEventListener('pointerup', this._handlePointerUp);
        this._targetElement.addEventListener('pointermove', this._handlePointerMove);
        this._targetElement.addEventListener('wheel', this._handleMouseWheel);
    }

    /**
     * Handle pointer down events (mouse buttons)
     */
    private _handlePointerDown(event : PointerEvent) : void
    {
        if(event.pointerType !== 'mouse') { return; }

        const buttonKey = `button-${ event.button }`;
        this._buttonState[buttonKey] = { pressed: true };

        this._notifyInputChanged({
            type: 'mouse',
            event,
            buttons: { ...this._buttonState },
            axes: { ...this._axesState },
            position: { ...this._position },
        });
    }

    /**
     * Handle pointer up events (mouse buttons)
     */
    private _handlePointerUp(event : PointerEvent) : void
    {
        if(event.pointerType !== 'mouse') { return; }

        const buttonKey = `button-${ event.button }`;
        this._buttonState[buttonKey] = { pressed: false };

        this._notifyInputChanged({
            type: 'mouse',
            event,
            buttons: { ...this._buttonState },
            axes: { ...this._axesState },
            position: { ...this._position },
        });
    }

    /**
     * Handle pointer move events (mouse position)
     */
    private _handlePointerMove(event : PointerEvent) : void
    {
        if(event.pointerType !== 'mouse') { return; }

        this._position = {
            absolute: {
                x: event.clientX,
                y: event.clientY,
            },
            relative: {
                x: event.movementX,
                y: event.movementY,
            },
        };

        this._axesState['axis-x'] = event.clientX;
        this._axesState['axis-y'] = event.clientY;

        this._notifyInputChanged({
            type: 'mouse',
            event,
            buttons: { ...this._buttonState },
            axes: { ...this._axesState },
            position: { ...this._position },
        });
    }

    /**
     * Handle mouse wheel events
     */
    private _handleMouseWheel(event : WheelEvent) : void
    {
        this._wheelState = {
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            deltaZ: event.deltaZ,
            deltaMode: event.deltaMode,
        };

        this._notifyInputChanged({
            type: 'mouse',
            event,
            buttons: { ...this._buttonState },
            axes: { ...this._axesState },
            position: { ...this._position },
            wheel: { ...this._wheelState },
        });
    }

    /**
     * Notify subscribers of device connected event
     */
    private _notifyDeviceConnected() : void
    {
        if(this._onDeviceConnected)
        {
            this._onDeviceConnected(this._mouseDevice);
        }
    }

    /**
     * Notify subscribers of input changed event
     */
    private _notifyInputChanged(state : MouseInputState) : void
    {
        if(this._onInputChanged)
        {
            this._onInputChanged(this._mouseDevice, state);
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------
